/**
 * Task Board API Service — Phase 2
 *
 * API contract (backend must implement these endpoints):
 *   GET    /tasks?assignee_id=1        → ApiResponse<Task[]>
 *   POST   /tasks                      → ApiResponse<Task>
 *   PATCH  /tasks/:id                  → ApiResponse<Task>
 *   PATCH  /tasks/:id/status           → ApiResponse<Task>
 *   POST   /tasks/:id/comments         → ApiResponse<Task>
 *
 * Toggle between mock and real API with VITE_TASK_BOARD_USE_API in .env
 */

import { apiService } from '../../../services/api';
import { Task, TaskStatus, TaskPriority, TaskTeam } from '../types';

// ── Request payload types ─────────────────────────────────────────────────────

export interface CreateTaskPayload {
  title: string;
  description: string;
  assignee_id: number;
  priority: TaskPriority;
  status?: TaskStatus;
  team: TaskTeam;
  due_date: string | null;
  labels: string[];
}

export interface MoveTaskPayload {
  status: TaskStatus;
  reason?: string;
}

export interface UpdateTaskPayload {
  title?: string;
  description?: string;
  assignee_id?: number;
  priority?: TaskPriority;
  team?: TaskTeam;
  due_date?: string | null;
  labels?: string[];
  status?: TaskStatus;
}

export interface AddCommentPayload {
  text: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

export class TaskApi {
  /** Fetch all tasks. Pass assignee_id to scope to one user's board. */
  static async getTasks(userId?: number): Promise<Task[]> {
    const params = userId !== undefined ? { assignee_id: userId } : {};
    const response = await apiService.get<Task[]>('/tasks', { params });
    return response.data;
  }

  /** Create a new task. Returns the persisted Task with server-generated id/key. */
  static async createTask(payload: CreateTaskPayload): Promise<Task> {
    const response = await apiService.post<Task>('/tasks', payload);
    return response.data;
  }

  /**
   * Move a task to a new status.
   * reason is required when moving to 'backlog' (enforced on backend too).
   */
  static async moveTask(taskId: string, payload: MoveTaskPayload): Promise<Task> {
    const response = await apiService.patch<Task>(`/tasks/${taskId}/status`, payload);
    return response.data;
  }

  /** Update any field on a task (title, description, assignee, priority, team, due date, labels). */
  static async updateTask(taskId: string, payload: UpdateTaskPayload): Promise<Task> {
    const response = await apiService.patch<Task>(`/tasks/${taskId}`, payload);
    return response.data;
  }

  /** Append a comment to a task. Returns the updated Task with new comment included. */
  static async addComment(taskId: string, payload: AddCommentPayload): Promise<Task> {
    const response = await apiService.post<Task>(`/tasks/${taskId}/comments`, payload);
    return response.data;
  }

  /** Fetch the canonical team list from the backend (falls back to DB DISTINCT query). */
  static async getTeams(): Promise<string[]> {
    const response = await apiService.get<string[]>('/tasks/teams');
    return response.data;
  }

  /** Ask AI whether a deprioritization reason is a meaningful sentence. */
  static async validateReason(reason: string): Promise<{ valid: boolean }> {
    const response = await apiService.post<{ valid: boolean }>('/tasks/ai/validate-reason', { reason });
    return response.data;
  }

  /** Fetch all active employees for assignee dropdown and @-mention. */
  static async getMembers(): Promise<{ id: number; name: string; email: string; department: string }[]> {
    const response = await apiService.get<{ id: number; name: string; email: string; department: string }[]>('/tasks/members');
    return response.data;
  }
}
